import { Button, useDisclosure } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { TokensTable } from "../components/TokensTable";
import { TransferFailedModal } from "../components/transfers/TransferFailedModal";
import { TransfersForm } from "../components/transfers/TransferForm";
import { TransfersReviewModal } from "../components/transfers/TransferReviewModal";
import { TransfersSuccessModal } from "../components/transfers/TransferSuccessModal";
import { TransferSuccessToast } from "../components/transfers/TransferSuccessToast";
import { TransfersWaitingModal } from "../components/transfers/TransferWaitingModal";
import { TransferWaitingToast } from "../components/transfers/TransferWaitingToast";
import type { TransferFormData, TransferTxFailed, TransferTxSuccess } from "../components/transfers/transfersConfig";
import { useTransfersMutation } from "../mutations/aztec";
import { l2AccountAddressQuery } from "../queries/aztec";

export default function TransfersRoute() {
  const { data: l2AccountAddress } = useQuery(l2AccountAddressQuery);

  const transferFormDataRef = useRef<TransferFormData | null>(null);
  const transferSuccessRef = useRef<TransferTxSuccess | null>(null);
  const transferFailedRef = useRef<TransferTxFailed | null>(null);
  const [pendingTxHash, setPendingTxHash] = useState<string | undefined>();

  const reviewModalDisclosure = useDisclosure();
  const [isMinimized, setMinimized] = useState(false);

  const transfersMutation = useTransfersMutation({
    onSuccess: (transferSuccess) => {
      transferSuccessRef.current = transferSuccess;
      transferFailedRef.current = null;
      setPendingTxHash(undefined);
    },
    onError: (transferError) => {
      transferFailedRef.current = transferError;
      transferSuccessRef.current = null;
      setPendingTxHash(undefined);
    },
    onTxSubmitted: (txHash) => {
      setPendingTxHash(txHash);
    },
  });

  function onReview(transferFormData: TransferFormData) {
    transferFormDataRef.current = transferFormData;
    reviewModalDisclosure.onOpen();
  }

  function onTransfer() {
    if (!transferFormDataRef.current) {
      throw new Error("Transfer form data is not valid");
    }

    reviewModalDisclosure.onClose();
    transfersMutation.mutate(transferFormDataRef.current);
  }

  if (!l2AccountAddress) {
    return (
      <div className="flex flex-col items-center gap-4 max-w-screen-xl mx-auto mt-8 p-4">
        Your Aztec Wallet is not connected.
        <Button
          fullWidth={false}
          size="lg"
          color="default"
          radius="full"
          className="font-semibold h-16"
          onPress={() => {
            document.getElementById("connect-wallet-btn")?.click();
          }}
        >
          Connect Wallet
        </Button>
      </div>
    );
  }

  const shouldShowWaiting = !reviewModalDisclosure.isOpen && transfersMutation.status === "pending";
  const shouldShowSuccess = transfersMutation.status === "success";
  const shouldShowError = transfersMutation.status === "error";

  return (
    <div className="max-w-screen-xl mx-auto my-12 grid grid-cols-12 gap-x-4 gap-y-8">
      <div className="flex flex-col gap-4 col-span-6">
        <h1 className="font-['Krona_One'] text-4xl">Transfers</h1>

        <p className="text-[#849AA9] text-sm font-medium">
          Shielding is the process during which the balance of your token, which is public, becomes private, as a result
          of it. <span className="text-[#14DBC0] font-semibold">Learn more</span>
        </p>
      </div>

      <TransfersForm l2AccountAddress={l2AccountAddress} onReview={onReview} />

      <TokensTable/>

      <TransfersReviewModal
        isOpen={reviewModalDisclosure.isOpen}
        onClose={reviewModalDisclosure.onClose}
        transferFormData={transferFormDataRef.current}
        onTransfer={onTransfer}
      />

      <TransfersWaitingModal
        isOpen={shouldShowWaiting && !isMinimized}
        onClose={() => {
          setMinimized(true);
        }}
        txHash={pendingTxHash}
        tokenSymbol={transferFormDataRef.current?.tokenSymbol}
      />

      {shouldShowWaiting && isMinimized && (
        <TransferWaitingToast txHash={pendingTxHash} onDismiss={() => setMinimized(false)} />
      )}

      <TransfersSuccessModal
        isOpen={shouldShowSuccess && !isMinimized}
        onClose={() => {
          transfersMutation.reset();
        }}
        transferResult={transferSuccessRef.current}
      />

      {shouldShowSuccess && isMinimized && (
        <TransferSuccessToast
          amount={transferFormDataRef.current?.displayAmount}
          tokenSymbol={transferFormDataRef.current?.tokenSymbol}
          txHash={transferSuccessRef.current?.txHash}
          onDismiss={() => {
            transfersMutation.reset();
            setMinimized(false);
          }}
        />
      )}

      <TransferFailedModal
        isOpen={shouldShowError && !isMinimized}
        onClose={() => {
          transfersMutation.reset();
        }}
        failedTxData={transferFailedRef.current}
      />
    </div>
  );
}
